import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { adminApi } from 'admin/reduxQuery'
import { routerMiddleware } from 'connected-react-router'
import { engageToolsApi } from 'engageTools/reduxApi'
import { ComponentsService } from 'engageTools/studio/componentsService'
import { FeatureService } from 'features/featureService'
import { History } from 'history'
import { ImpersonationService } from 'http/impersonationService'
import { statsApi } from 'http/reduxQuery'
import { ordersApi } from 'orders/reduxQueries'
import { pricingApi } from 'products/pricing/reduxQueries'
import { articleApi } from 'products/reduxApi'
import { discountsApi } from 'discounts/reduxQuery'
import { Store } from 'redux'
import { integrationsApi } from 'settings/reduxQuery'
import { discountUploadMiddleware } from 'discounts/discountUploadMiddleware'
import { parkMapApi } from 'venue/parkMap/reduxQuery'
import { audienceApi } from '../crm/audiences/reduxQuery'
import { pushNotificationsApi } from '../pushNotifications/reduxQuery'
import { State } from './interface'
import { supersetApi } from 'reports/supersetQuery'
import { allReducers } from './reducers'
import { taxConfigurationsApi } from 'settings/accountSettings/taxConfigurations/rtkQuery'
import { bookingFeesApi } from 'settings/accountSettings/bookingFees/reduxQuery'
import { membershipsApi } from 'memberships/reduxQueries'
import { reportingCategoriesApi } from 'settings/accountSettings/reportingCategories/reduxQuery'
import { posPaymentMethodsApi } from 'settings/accountSettings/paymentMethods/pos/reduxQuery'
import { globalSearchApi } from 'globalSearch/reduxQueries'
import { auditLogApi } from 'auditLog/reduxQuery'
import { pulseApi } from 'pulse/reduxQueries'
import { serviceAppsApi } from 'settings/personalSettings/serviceApps/reduxQuery'

export interface StoreServices {
    featureService: FeatureService
    componentsService: ComponentsService
    impersonationService: ImpersonationService
    history: History
}

export function createReduxStore(initialState: State, storeServices: StoreServices): Store<State, any> {
    const store = configureStore({
        reducer: allReducers(storeServices.history),
        preloadedState: initialState,
        devTools: process.env.NODE_ENV !== 'production',
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({ thunk: { extraArgument: storeServices }, serializableCheck: false })
                .concat(engageToolsApi.middleware)
                .concat(ordersApi.middleware)
                .concat(integrationsApi.middleware)
                .concat(articleApi.middleware)
                .concat(discountsApi.middleware)
                .concat(routerMiddleware(storeServices.history))
                .concat(parkMapApi.middleware)
                .concat(audienceApi.middleware)
                .concat(pricingApi.middleware)
                .concat(statsApi.middleware)
                .concat(adminApi.middleware)
                .concat(pushNotificationsApi.middleware)
                .concat(supersetApi.middleware)
                .concat(taxConfigurationsApi.middleware)
                .concat(bookingFeesApi.middleware)
                .concat(membershipsApi.middleware)
                .concat(reportingCategoriesApi.middleware)
                .concat(posPaymentMethodsApi.middleware as any)
                .concat(globalSearchApi.middleware)
                .concat(auditLogApi.middleware)
                .concat(pulseApi.middleware)
                .concat(serviceAppsApi.middleware)
                .prepend(discountUploadMiddleware.middleware),
    })

    setupListeners(store.dispatch)

    // Vite HMR: global is set by inline script in index.html so we avoid import.meta here (Jest uses module:commonjs)
    const hot =
        typeof globalThis !== 'undefined' &&
        (globalThis as { __vite_import_meta?: { hot?: { accept: (path: string, cb: (m: unknown) => void) => void } } })
            .__vite_import_meta?.hot
    if (hot) {
        hot.accept('./reducers', (newModule: unknown) => {
            const m = newModule as { allReducers?: (h: History) => ReturnType<typeof allReducers> } | null
            if (m?.allReducers) store.replaceReducer(m.allReducers(storeServices.history))
        })
    }

    return store
}
